import config from '@src/config'

import type {
  AiCustomJobDefinitionV1CreateInput,
  AiCustomJobDefinitionV1List,
  AiCustomJobDefinitionV1ListInput,
  AiCustomJobDefinitionV1UpdateInput,
  CodableAiCustomJobV1,
} from './ai-client/aiCustomJobDefinitionV1Schema'
import type {
  AiJobDefinitionByIdInput,
  AiJobDefinitionList,
  AiJobDefinitionListInput,
  CodableAiJobDefinition,
} from './ai-client/aiJobDefinitionSchema'
import type {
  AiTriggerDefinition,
  AiTriggerDefinitionCreateInput,
  AiTriggerDefinitionDeleteInput,
  AiTriggerDefinitionByIdInput,
  AiTriggerDefinitionList,
  AiTriggerDefinitionListInput,
  AiTriggerDefinitionUpdateInput,
  AiTriggerDefinitionValidateInput,
  AiTriggerDefinitionValidateExistingInput,
} from './ai-client/aiTriggerDefinitionSchema'
import type Transport from './index'
import { HttpTransaction } from './transaction'

export interface PromptSettings {
  id: string
  phoneNumberId: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

type AgentResourceDataItem = {
  storageBucket: string
  storageKey: string
}

type AgentResourceContentItem = {
  content: string
}

export type AgentResourceResponse = {
  id: string
  version: number
}

export type WebsiteImportResponse = {
  title: string
  content: string
  metadata: {
    isAuthorized: boolean
  }
  sourceType: string
  sourceValue: string
}

export type WebsiteSourceResponse = {
  title: string
  description: string
  image: string
  url: string
  siteName: string
  type: string
}

export interface AgentResource {
  createdAt: string
  updatedAt: string
  deletedAt?: string
  version: number
  createdBy: string
  updatedBy: string
  deletedBy?: string
  id: string
  status: 'processing' | 'completed' | 'failed'
  statusReason?: string
  data: AgentResourceDataItem | AgentResourceContentItem
  mimeType: string
  filename: string
  title: string
  emoji?: string
  sources?: {
    type: 'url'
    value: string
  }[]
}

export interface AgentResourceInput {
  title: string
  content: string
  sources?: { type: 'url'; value: string }[]
  format?: 'markdown' | 'text'
}

export interface AgentResourceUpdateInput extends AgentResourceInput {
  id: string
}

export default class AiClient {
  constructor(private transport: Transport) {}
  automations = {
    getResourceById: (id: string): Promise<AgentResource> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-resources/${id}`,
          method: 'GET',
        }),
      )
    },
    getResources: (): Promise<{
      data: AgentResource[]
      totalItems: number
      nextPageToken: string
    }> => {
      const params = new URLSearchParams({
        maxResults: '1000',
      })

      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-resources?${params.toString()}`,
          method: 'GET',
        }),
      )
    },
    uploadResource: (files: File[]): Promise<AgentResource[]> => {
      const formData = new FormData()
      files.forEach((file) => {
        // workaround for https://github.com/react-dropzone/react-dropzone/issues/1418
        // For markdown files, ensure correct MIME type
        if (file.name.toLowerCase().endsWith('.md')) {
          // Create a new blob with the correct MIME type
          const blob = new Blob([file], { type: 'text/markdown' })
          formData.append('files', blob, file.name)
        } else {
          formData.append('files', file)
        }
      })

      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-resources/file`,
          method: 'POST',
          body: formData,
        }),
      )
    },
    createResourcePage: ({
      title,
      content,
      sources,
      format,
    }: AgentResourceInput): Promise<{
      id: string
      version: number
      sources: { type: 'url'; value: string }[]
      format: 'markdown' | 'text'
    }> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-resources/text`,
          method: 'POST',
          body: {
            title,
            content,
            sources,
            format,
          },
        }),
      )
    },
    updateResourcePage: ({
      id,
      title,
      content,
      sources,
    }: AgentResourceUpdateInput): Promise<{
      id: string
      version: number
    }> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-resources/text/${id}`,
          method: 'PUT',
          body: {
            title,
            content,
            sources,
          },
        }),
      )
    },
    deleteResource: (id: string) => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-resources/${id}`,
          method: 'DELETE',
        }),
      )
    },
  }

  /**
   * @deprecated
   * TODO: Remove this once we have migrated all usages to agentDefinitions
   * and the feature flag is 100% rollout for Custom Jobs V1
   * https://linear.app/openphone/issue/PRODU-1183/remove-feature-flag-webaiagentcustomjobsv1
   */
  triggerDefinitions = {
    getById: (
      input: AiTriggerDefinitionByIdInput,
      signal?: AbortSignal,
    ): Promise<AiTriggerDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-definitions/${input.id}`,
          method: 'GET',
          signal,
          query: {
            version:
              typeof input.version !== 'undefined' ? `${input.version}` : undefined,
          },
        }),
      )
    },
    getAll: (
      input: AiTriggerDefinitionListInput,
      signal?: AbortSignal,
    ): Promise<AiTriggerDefinitionList> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-definitions`,
          method: 'GET',
          signal,
          query: {
            maxResults: `${input.maxResults ?? 1000}`,
            pageToken: input.pageToken ? `${input.pageToken}` : undefined,
            entityIds: input.entityIds ? `${input.entityIds.join(',')}` : undefined,
          },
        }),
      )
    },
    create: (
      input: AiTriggerDefinitionCreateInput,
      signal?: AbortSignal,
    ): Promise<AiTriggerDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-definitions`,
          method: 'POST',
          body: input,
          skipPersisting: true,
          signal,
        }),
      )
    },
    update: (
      input: AiTriggerDefinitionUpdateInput,
      signal?: AbortSignal,
    ): Promise<AiTriggerDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-definitions/${input.id}`,
          method: 'PUT',
          body: input,
          skipPersisting: true,
          signal,
        }),
      )
    },
    delete: (
      input: AiTriggerDefinitionDeleteInput,
      signal?: AbortSignal,
    ): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-definitions/${input.id}`,
          method: 'DELETE',
          skipPersisting: true,
          signal,
        }),
      )
    },
    validate: (
      input: AiTriggerDefinitionValidateInput,
      signal?: AbortSignal,
    ): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-definitions/validate`,
          method: 'POST',
          body: input,
          signal,
        }),
      )
    },
    validateExisting: (
      input: AiTriggerDefinitionValidateExistingInput,
      signal?: AbortSignal,
    ): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-definitions/${input.id}/validate`,
          method: 'POST',
          body: input,
          signal,
        }),
      )
    },
  }
  agentDefinitions = {
    getById: (
      input: AiTriggerDefinitionByIdInput,
      signal?: AbortSignal,
    ): Promise<AiTriggerDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL_V2}agent-definitions/${input.id}`,
          method: 'GET',
          signal,
          query: {
            version:
              typeof input.version !== 'undefined' ? `${input.version}` : undefined,
          },
        }),
      )
    },
    getAll: (
      input: AiTriggerDefinitionListInput,
      signal?: AbortSignal,
    ): Promise<AiTriggerDefinitionList> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL_V2}agent-definitions`,
          method: 'GET',
          signal,
          query: {
            maxResults: `${input.maxResults ?? 1000}`,
            pageToken: input.pageToken ? `${input.pageToken}` : undefined,
            entityIds: input.entityIds ? `${input.entityIds.join(',')}` : undefined,
          },
        }),
      )
    },
    create: (
      input: AiTriggerDefinitionCreateInput,
      signal?: AbortSignal,
    ): Promise<AiTriggerDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL_V2}agent-definitions`,
          method: 'POST',
          body: input,
          skipPersisting: true,
          signal,
        }),
      )
    },
    update: (
      input: AiTriggerDefinitionUpdateInput,
      signal?: AbortSignal,
    ): Promise<AiTriggerDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL_V2}agent-definitions/${input.id}`,
          method: 'PUT',
          body: input,
          skipPersisting: true,
          signal,
        }),
      )
    },
    delete: (
      input: AiTriggerDefinitionDeleteInput,
      signal?: AbortSignal,
    ): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-definitions/${input.id}`,
          method: 'DELETE',
          skipPersisting: true,
          signal,
        }),
      )
    },
    validate: (
      input: AiTriggerDefinitionValidateInput,
      signal?: AbortSignal,
    ): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL_V2}agent-definitions/validate`,
          method: 'POST',
          body: input,
          signal,
        }),
      )
    },
    validateExisting: (
      input: AiTriggerDefinitionValidateExistingInput,
      signal?: AbortSignal,
    ): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL_V2}agent-definitions/${input.id}/validate`,
          method: 'POST',
          body: input,
          signal,
        }),
      )
    },
  }
  /**
   * TODO: Remove this once we do 100% rollout for Custom Jobs V1
   * https://linear.app/openphone/issue/PRODU-1183/remove-feature-flag-webaiagentcustomjobsv1
   */
  jobDefinitions = {
    getAll: (
      input?: AiJobDefinitionListInput,
      signal?: AbortSignal,
    ): Promise<AiJobDefinitionList> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-job-definitions`,
          method: 'GET',
          signal,
          query: {
            maxResults: `${input?.maxResults ?? 10}`,
            pageToken: input?.pageToken ? `${input.pageToken}` : undefined,
          },
        }),
      )
    },
    getById: (
      input: AiJobDefinitionByIdInput,
      signal?: AbortSignal,
    ): Promise<CodableAiJobDefinition> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-job-definitions/${input.id}`,
          method: 'GET',
          signal,
          query: {
            version:
              typeof input.version !== 'undefined' ? `${input.version}` : undefined,
          },
        }),
      )
    },
  }
  customJobsV1 = {
    getAll: (
      input?: AiCustomJobDefinitionV1ListInput,
      signal?: AbortSignal,
    ): Promise<AiCustomJobDefinitionV1List> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL_V2}agent-job-definitions`,
          method: 'GET',
          signal,
          query: {
            maxResults: `${input?.maxResults ?? 1000}`,
            pageToken: input?.pageToken,
          },
        }),
      )
    },
    create: (
      input: AiCustomJobDefinitionV1CreateInput,
      signal?: AbortSignal,
    ): Promise<CodableAiCustomJobV1> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-job-definitions`,
          method: 'POST',
          body: input,
          skipPersisting: true,
          signal,
        }),
      )
    },
    update: (
      input: AiCustomJobDefinitionV1UpdateInput,
      signal?: AbortSignal,
    ): Promise<CodableAiCustomJobV1> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-job-definitions/${input.id}`,
          method: 'PUT',
          body: input,
          skipPersisting: true,
          signal,
        }),
      )
    },
    delete: (id: string): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}agent-job-definitions/${id}`,
          method: 'DELETE',
        }),
      )
    },
  }
  prompt = {
    toggle: (promptId: string, isActive: boolean): Promise<PromptSettings> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}config/tag-settings/${promptId}/toggle`,
          method: 'PUT',
          body: {
            isActive,
          },
        }),
      )
    },

    get: (phoneNumberId: string): Promise<PromptSettings> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}config/tag-settings/phone-number/${phoneNumberId}`,
          method: 'GET',
        }),
      )
    },
  }
  website = {
    import: (websiteUrl: string): Promise<WebsiteImportResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}data/convert`,
          method: 'POST',
          body: { inputType: 'url', inputValue: websiteUrl, outputFormat: 'markdown' },
        }),
      )
    },
    getWebsiteSource: (websiteUrl: string): Promise<WebsiteSourceResponse> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.AI_SERVICE_URL}metadata/website`,
          method: 'GET',
          query: {
            url: websiteUrl,
          },
        }),
      )
    },
  }
}
